import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";

import PageHeader from "../../components/PageHeader/PageHeader";
import KpiCard from "../../components/KpiCard/KpiCard";
import Button from "../../components/Button/Button";
import { Tag } from "../../components/Tag/Tag";

import { useToast } from "../../context/ToastContext";
import { getColorBaseTokenValue, hexToRgba } from "../../design-tokens";

import ProductionApi, {
  GrossVolumeResponse,
  GrossVolumePoint,
} from "../../services/ProductionApi";
import useLiveRefresh from "../../hooks/useLiveRefresh";
import { useShiftList } from "../../hooks/useWorkSchedule";
import ShiftFilter from "../../components/ShiftFilter/ShiftFilter";
import PeriodFilter from "../../components/PeriodFilter/PeriodFilter";
import { useActivityTracking } from "../../hooks/useActivityTracking";
import { ActivityEventType } from "../../utilities/activityLog";
import {
  describeSelection,
  addDays,
  DEFAULT_PERIOD_SELECTION,
  PeriodSelection,
  selectionDates,
  selectionReachesToday,
  toYmd,
} from "../../utilities/reportRange";
import ProductionChart from "./ProductionChart";
import styles from "./ReportGrossVolumeProduced.module.css";

/** Dias projetados à frente de hoje (habilita o segmento de projeção no backend). */
const PROJECTION_DAYS = 7;

const ReportGrossVolumeProduced: React.FC = () => {
  const { t, i18n } = useTranslation("grossVolume");
  const { displayToast } = useToast();
  // Log de atividade: emitido SÓ nos handlers abaixo. Instrumentar dentro de load()
  // geraria evento falso a cada push do SignalR (useLiveRefresh).
  const track = useActivityTracking();

  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";

  const [data, setData] = useState<GrossVolumeResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string>("");

  // Filtro aplicado x pendente (só dispara no "Pesquisar")
  const [activePeriod, setActivePeriod] = useState<PeriodSelection>(DEFAULT_PERIOD_SELECTION);
  // `null` = "Personalizado" escolhido sem as duas datas ainda → "Aplicar" desabilitado.
  const [pendingPeriod, setPendingPeriod] = useState<PeriodSelection | null>(
    DEFAULT_PERIOD_SELECTION,
  );

  const shifts = useShiftList();
  const [shiftId, setShiftId] = useState<string | null>(null);

  const formatNumber = useCallback(
    (value: number, decimals = 0): string =>
      Number(value).toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }),
    [locale],
  );

  const formatDate = useCallback(
    (ymd: string, withYear = false): string => {
      const [y, m, d] = ymd.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(locale, {
        day: "2-digit",
        month: "short",
        ...(withYear ? { year: "numeric" } : {}),
      });
    },
    [locale],
  );

  /**
   * Resolve a janela {from,to} da seleção. `to` ganha +PROJECTION_DAYS porque o
   * backend só devolve o segmento projetado quando `to` é FUTURO — sem isso a
   * linha de projeção e o KPI de projeção vêm vazios.
   *
   * O empurrão só se aplica quando a janela alcança hoje: um intervalo
   * customizado puramente histórico não tem projeção a pedir, e nesse caso
   * mandamos a data final escolhida como está.
   */
  const resolveRange = useCallback(
    (sel: PeriodSelection): { from: string; to: string } => {
      const { from, to } = selectionDates(sel);
      return {
        from: toYmd(from),
        to: toYmd(selectionReachesToday(sel) ? addDays(to, PROJECTION_DAYS) : to),
      };
    },
    [],
  );

  const load = useCallback(
    async (sel: PeriodSelection, sid: string | null): Promise<void> => {
      setLoading(true);
      try {
        const resp = await ProductionApi.GetGrossVolume({ ...resolveRange(sel), ...(sid ? { shiftId: sid } : {}) });
        setData((resp?.data as GrossVolumeResponse) ?? null);
        setActivePeriod(sel);
        setLastUpdated(new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }));
      } catch (err: any) {
        // Erro transitório (inclui refresh em tempo real em segundo plano): apenas notifica,
        // NÃO reseta o preset aplicado nem refaz a busca default.
        displayToast("error", t("loadErrorTitle"), err?.errors?.[0] ?? t("loadErrorMsg"), false, 6000);
      } finally {
        setLoading(false);
      }
    },
    [resolveRange, displayToast, t, locale],
  );

  // Carga inicial: janela default.
  useEffect(() => {
    load(DEFAULT_PERIOD_SELECTION, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualização em tempo real via SignalR (substitui o botão "Atualizar"). Uma
  // janela puramente histórica não muda — não vale reconsultar a cada evento.
  useLiveRefresh(
    () => void load(activePeriod, shiftId),
    selectionReachesToday(activePeriod),
  );

  const handleApply = (): void => {
    if (!pendingPeriod) return; // intervalo customizado incompleto
    track(ActivityEventType.FilterApply, describeSelection(pendingPeriod));
    void load(pendingPeriod, shiftId);
  };
  const handleReset = (): void => {
    track(ActivityEventType.FilterReset);
    setPendingPeriod(DEFAULT_PERIOD_SELECTION);
    setShiftId(null);
    void load(DEFAULT_PERIOD_SELECTION, null);
  };
  // O turno recarrega na hora (sem "Aplicar"), então carrega com a janela ATIVA.
  const handleShiftChange = (id: string | null): void => {
    track(ActivityEventType.ShiftChange, { shiftId: id });
    setShiftId(id);
    void load(activePeriod, id);
  };

  // ---- Derivações da resposta ----
  const derived = useMemo(() => {
    const series: GrossVolumePoint[] = data?.series ?? [];
    const projection: GrossVolumePoint[] = data?.projection ?? [];
    const dailyTarget = data?.dailyTarget ?? 0;

    // Neutraliza a pendência #002 no cliente: a série real termina onde a projeção
    // começa (fronteira do backend), descartando zeros futuros sobrepostos.
    const projStart = projection.length ? projection[0].date : null;
    let realized = projStart ? series.filter((p) => p.date < projStart) : series;
    if (realized.length === 0) realized = series;

    const todayIndex = realized.length - 1;
    const noData = realized.length === 0 || realized.every((p) => p.produced === 0);

    const volume = realized.reduce((acc, p) => acc + p.produced, 0);
    const productiveDays = realized.filter((p) => p.produced > 0).length;
    const avgDaily = productiveDays > 0 ? volume / productiveDays : 0;
    const avgDelta = dailyTarget > 0 ? ((avgDaily - dailyTarget) / dailyTarget) * 100 : 0;

    const projectionAvailable = !!data?.projectionAvailable && projection.length > 0;
    const projectedTotal = projection.reduce((acc, p) => acc + p.produced, 0);
    const close = projectionAvailable ? volume + projectedTotal : 0;
    const horizonDays = realized.length + projection.length;
    const targetHorizon = dailyTarget * horizonDays;
    const projPct = targetHorizon > 0 ? (close / targetHorizon) * 100 : 0;

    // Séries alinhadas para o gráfico
    const allPoints = [...realized, ...projection];
    const rawDates = allPoints.map((p) => p.date);
    const labels = rawDates.map((ymd) => formatDate(ymd));
    const realizedData: Array<number | null> = [
      ...realized.map((p) => p.produced),
      ...projection.map(() => null),
    ];
    const metaData = allPoints.map(() => dailyTarget);
    const projectedData: Array<number | null> = allPoints.map(() => null);
    if (projectionAvailable && todayIndex >= 0) {
      projectedData[todayIndex] = realized[todayIndex].produced; // junção com o traço real
      projection.forEach((p, j) => {
        projectedData[realized.length + j] = p.produced;
      });
    }

    // Rótulo cobre só o realizado (não o `to` futuro que habilita a projeção).
    const rangeLabel =
      realized.length > 0
        ? `${formatDate(realized[0].date, true)} – ${formatDate(realized[realized.length - 1].date, true)}`
        : "";

    return {
      series,
      dailyTarget,
      realized,
      todayIndex,
      noData,
      volume,
      productiveDays,
      avgDaily,
      avgDelta,
      projectionAvailable,
      close,
      projPct,
      rawDates,
      labels,
      realizedData,
      metaData,
      projectedData,
      rangeLabel,
    };
  }, [data, formatDate]);

  const chartColors = useMemo(
    () => ({
      realized: getColorBaseTokenValue("color.secondary.500"),
      realizedFill: hexToRgba(getColorBaseTokenValue("color.secondary.500"), 0.1),
      meta: getColorBaseTokenValue("color.neutral.500"),
      projected: getColorBaseTokenValue("color.feedback.warning.500"),
      todayLine: getColorBaseTokenValue("color.neutral.500"),
      lastPoint: getColorBaseTokenValue("color.primary.500"),
      text: getColorBaseTokenValue("color.neutral.500"),
      grid: getColorBaseTokenValue("color.neutral.100"),
    }),
    [],
  );

  const showChart = data && !derived.noData;
  const projectionNote = derived.productiveDays < 5 ? t("shortWarn") : t("projectionUnavailable");

  return (
    <div className={styles.page}>
      <PageHeader title={t("title")} description={t("subtitle")} className={styles.pageHeaderFlush}>
        <Tag
          label={lastUpdated ? `${t("updatedAt")} ${lastUpdated}` : t("loadingLabel")}
          severity="successInverse"
          icon="sync"
        />
      </PageHeader>

      {/* Filtros */}
      <div className={`${styles.card} ${styles.filters}`}>
        <PeriodFilter
          value={pendingPeriod}
          onChange={setPendingPeriod}
          rangeLabel={derived.rangeLabel || t("noRange")}
          label={t("fPeriod")}
          presetLabels={{ "7": t("preset7"), "14": t("preset14"), "30": t("preset30") }}
        />

        <ShiftFilter value={shiftId} onChange={handleShiftChange} shifts={shifts} />

        <div className={styles.filterSpacer} />

        <div className={styles.applyWrap}>
          <Button
            label={t("apply")}
            icon="check"
            severity="secondary"
            onClick={handleApply}
            loading={loading}
            disabled={!pendingPeriod}
          />
        </div>
      </div>

      {/* KPIs */}
      <div className={styles.kpiRow}>
        <KpiCard
          title={t("kVolume")}
          value={derived.noData ? "—" : formatNumber(derived.volume)}
          icon="inventory_2"
          variant="primary"
          descriptionIcon="event_available"
          trend="neutral"
          description={`${derived.realized.length} ${t("daysInPeriod")}`}
        />
        <KpiCard
          title={t("kAvg")}
          value={derived.noData ? "—" : formatNumber(derived.avgDaily, 1)}
          icon="speed"
          variant="info"
          descriptionIcon={derived.avgDelta >= 0 ? "arrow_upward" : "arrow_downward"}
          trend={derived.avgDelta >= 0 ? "up" : "down"}
          description={`${derived.avgDelta >= 0 ? "+" : ""}${formatNumber(derived.avgDelta, 1)}% ${t("vsTarget")}`}
        />
        <KpiCard
          title={t("kProj")}
          value={derived.projectionAvailable ? formatNumber(derived.close) : "—"}
          icon="trending_up"
          variant="warning"
          descriptionIcon={derived.projectionAvailable ? "trending_up" : "info"}
          trend="warning"
          description={
            derived.projectionAvailable
              ? `${t("estimate")} · ${formatNumber(derived.projPct, 0)}% ${t("ofTarget")}`
              : t("projShort")
          }
        />
      </div>

      {/* Gráfico */}
      <section className={styles.card}>
        <div className={styles.chartHeader}>
          <span className={styles.chartTitle}>
            <span className="material-symbols-outlined">show_chart</span>
            {t("chartTitle")}
          </span>
          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={styles.legendSwatch} style={{ borderTopColor: chartColors.realized }} />
              {t("legRealized")}
            </span>
            <span className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ borderTopColor: chartColors.meta, borderTopStyle: "dashed" }}
              />
              {t("legMeta")}
            </span>
            <span className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ borderTopColor: chartColors.projected, borderTopStyle: "dashed" }}
              />
              {t("legProjected")}
            </span>
          </div>
        </div>

        <div className={styles.chartBody}>
          {loading && !data ? (
            <div className={styles.loading}>
              <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
            </div>
          ) : showChart ? (
            <>
              <ProductionChart
                rawDates={derived.rawDates}
                labels={derived.labels}
                realized={derived.realizedData}
                meta={derived.metaData}
                projected={derived.projectedData}
                todayIndex={derived.todayIndex}
                colors={chartColors}
                locale={locale}
                labelsText={{
                  realized: t("legRealized"),
                  meta: t("legMeta"),
                  projected: t("legProjected"),
                  today: t("today"),
                  unit: t("unitCars"),
                }}
              />
              {!derived.projectionAvailable && (
                <div className={styles.warnBanner}>
                  <span className="material-symbols-outlined">info</span>
                  <span className={styles.warnText}>{projectionNote}</span>
                </div>
              )}
            </>
          ) : (
            <div className={styles.empty}>
              <span className={`material-symbols-outlined ${styles.emptyIcon}`}>bar_chart_off</span>
              <div>
                <div className={styles.emptyTitle}>{t("emptyTitle")}</div>
                <div className={styles.emptySub}>{t("emptySub")}</div>
              </div>
              <Button
                label={t("clearFilters")}
                icon="restart_alt"
                severity="neutral"
                variant="outlined"
                onClick={handleReset}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReportGrossVolumeProduced;
